import TextInput from 'components/Common/Form/Input/TextInput'
import { useCallback, useContext, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Select from 'components/Common/Form/Input/Select'
import { getData as getCountryData } from 'country-list'
import AgentHubAddressAutocomplete from './AgentHubAddressAutocomplete'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import type { GooglePlaceDetails } from 'api/search'
import { mediaQueryUp } from 'components/utils/breakpoint'

const countryNames = getCountryData()
  .map((country) => country.name)
  .sort()

const AUSTRALIAN_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const

const AUSTRALIAN_STATE_NAMES_TO_CODES: Record<string, string> = {
  'australian capital territory': 'ACT',
  'new south wales': 'NSW',
  'northern territory': 'NT',
  queensland: 'QLD',
  'south australia': 'SA',
  tasmania: 'TAS',
  victoria: 'VIC',
  'western australia': 'WA',
}

function toAustralianStateCode(value: string): string {
  const code = value.trim().toUpperCase()
  if ((AUSTRALIAN_STATES as ReadonlyArray<string>).includes(code)) {
    return code
  }
  return AUSTRALIAN_STATE_NAMES_TO_CODES[value.trim().toLowerCase()] ?? ''
}

function normalizeCountryName(countryName: string): string {
  return countryName.toLowerCase().replace(/ \(the\)$/, '')
}

function matchCountryName(googleCountryName: string): string {
  if (!googleCountryName) {
    return ''
  }
  const normalizedName = normalizeCountryName(googleCountryName)
  const exactMatch = countryNames.find((countryName) => normalizeCountryName(countryName) === normalizedName)
  if (exactMatch) {
    return exactMatch
  }
  const partialMatches = countryNames.filter((countryName) => normalizeCountryName(countryName).includes(normalizedName))
  return partialMatches.sort((a, b) => a.length - b.length)[0] || googleCountryName
}

const FullWidthAutocompleteWrapper = styled.div`
  grid-column: 1 / -1;
  margin-bottom: ${rem(16)};
`

const AddressFields = styled.div`
  display: grid;
  gap: ${rem(12)};
`

const AddressRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

function getAddressValue(place: GooglePlaceDetails, type: string, nameKey: 'longName' | 'shortName' = 'longName') {
  const addressValue = place.find((component) => {
    return component.types?.includes(type)
  })?.[nameKey] || ''

  return addressValue
}

interface Props {
  initialStreetAddress?: string
  initialCity?: string
  initialStateOrTerritory?: string
  initialPostcode?: string
  initialCountry?: string
}

function AgentHubAddressInputFields({
  initialStreetAddress = '',
  initialCity = '',
  initialStateOrTerritory = '',
  initialPostcode = '',
  initialCountry = '',
}: Props) {
  const intl = useIntl()
  const [cityValue, setCityValue] = useState(initialCity)
  const [stateValue, setStateValue] = useState(initialStateOrTerritory)
  const [postcodeValue, setPostcodeValue] = useState(initialPostcode)
  const [countryValue, setCountryValue] = useState(initialCountry)

  const { currentRegionCode } = useContext(GeoContext)
  const isGBRegion = currentRegionCode === 'GB'
  const isAustraliaSelected = countryValue === 'Australia'

  const countyLabel = intl.formatMessage({
    id: '-l1w-ye38buF0jKWm4Lz',
    defaultMessage: 'County',
  })
  const stateLabel = intl.formatMessage({
    id: 'kbYah9u6iOc4Gc3TcUOa',
    defaultMessage: 'State or territory',
  })

  const onPlaceSelected = useCallback((searchResult: GooglePlaceDetails) => {
    if (searchResult) {
      const matchedCountry = matchCountryName(getAddressValue(searchResult, 'country'))

      if (matchedCountry === 'Australia') {
        setStateValue(toAustralianStateCode(getAddressValue(searchResult, 'administrative_area_level_1', 'shortName')))
      } else {
        setStateValue(getAddressValue(searchResult, isGBRegion ? 'administrative_area_level_2' : 'administrative_area_level_1'))
      }
      setPostcodeValue(getAddressValue(searchResult, 'postal_code'))
      setCityValue(getAddressValue(searchResult, 'locality') || getAddressValue(searchResult, 'administrative_area_level_2'))
      setCountryValue(matchedCountry)
    }
  }, [isGBRegion])

  function updateCityValue(event: React.ChangeEvent<HTMLInputElement>) {
    setCityValue(event.target.value)
  }

  function updateStateValue(event: React.ChangeEvent<HTMLInputElement>) {
    setStateValue(event.target.value)
  }

  function updateStateSelectValue(event: React.ChangeEvent<HTMLSelectElement>) {
    setStateValue(event.target.value)
  }

  function updatePostcodeValue(event: React.ChangeEvent<HTMLInputElement>) {
    setPostcodeValue(event.target.value)
  }

  function updateCountryValue(event: React.ChangeEvent<HTMLSelectElement>) {
    const nextCountry = event.target.value
    setCountryValue(nextCountry)
    if (nextCountry === 'Australia') {
      setStateValue((currentState) => toAustralianStateCode(currentState))
    } else if (countryValue === 'Australia') {
      setStateValue('')
    }
  }

  return (
    <AddressFields>
      <FullWidthAutocompleteWrapper>
        <AgentHubAddressAutocomplete
          onSelect={onPlaceSelected}
          inputName="streetAddress"
          defaultValue={initialStreetAddress}
          label={intl.formatMessage({
            id: 'E0BJ86Gb6dTyvhI4UZGO',
            defaultMessage: 'Street address',
          })}
          placeholder={intl.formatMessage({
            id: 'fjH3HDy5S5XlxIAQ58S-',
            defaultMessage: 'Search by address',
          })}
        />
      </FullWidthAutocompleteWrapper>

      <AddressRow>
        <TextInput
          label={intl.formatMessage({
            id: 'dGoddFuAHtBu5-ZuFBI2',
            defaultMessage: 'City or suburb',
          })}
          name="city"
          placeholder={intl.formatMessage({
            id: 'L-tIqZ2YMHvaCV138IVf',
            defaultMessage: 'Enter city or suburb',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'nxrSQfwvYaaKOC0f1TFh',
            defaultMessage: 'Please enter city or suburb',
          })}
          value={cityValue}
          onChange={updateCityValue}
        />
        {isAustraliaSelected && <Select
          label={stateLabel}
          name="stateOrTerritory"
          placeholder={intl.formatMessage({
            id: '0vqRbxbRCJxMlmqSnHkd',
            defaultMessage: 'Select state or territory',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'Bnje2MPWpCSeTzK5UrAX',
            defaultMessage: 'Please select a state or territory',
          })}
          value={stateValue}
          onChange={updateStateSelectValue}
        >
          {AUSTRALIAN_STATES.map((stateCode) => (
            <option key={stateCode} value={stateCode}>
              {stateCode}
            </option>
          ))}
        </Select>}
        {!isAustraliaSelected && <TextInput
          label={isGBRegion ? countyLabel : stateLabel}
          name="stateOrTerritory"
          placeholder={intl.formatMessage({
            id: 'lzfauHzRVTnGFWwMgud0',
            defaultMessage: 'Enter state or territory',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'px8iCu7Dk6lBWh2tKCgZ',
            defaultMessage: 'Please enter state or territory',
          })}
          value={stateValue}
          onChange={updateStateValue}
        />}
      </AddressRow>
      <AddressRow>
        <TextInput
          id="postcode"
          label={intl.formatMessage({
            id: '0eedaGBenbjyaOWpzrZY',
            defaultMessage: 'Postcode',
          })}
          type="text"
          name="postcode"
          placeholder={intl.formatMessage({
            id: 'HP5SHVUaCi_yDAckb9lg',
            defaultMessage: 'Enter postcode',
          })}
          noValidationSpacing
          pattern="^[^\.$\%\&\(\)\*!,@#]*$"
          invalidErrorMessage={intl.formatMessage({
            id: 'LsbNxIqz_fHdn11Tuoi9',
            defaultMessage: 'Please enter a valid postcode',
          })}
          minLength={3}
          maxLength={15}
          required
          autoComplete="postal-code"
          value={postcodeValue}
          onChange={updatePostcodeValue}
        />
        <Select
          label={intl.formatMessage({
            id: 'YUdak0pSLj2BylAM8wXQ',
            defaultMessage: 'Country',
          })}
          name="country"
          placeholder={intl.formatMessage({
            id: 'oqUVQsstYQBNxGln-uaj',
            defaultMessage: 'Select country',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: '4P3OZTqg5mYwuR8lX8PS',
            defaultMessage: 'Please select a country',
          })}
          autoComplete="country-name"
          value={countryValue}
          onChange={updateCountryValue}
        >
          {countryNames.map((countryName) => (
            <option key={countryName} value={countryName}>
              {countryName}
            </option>
          ))}
        </Select>
      </AddressRow>
    </AddressFields>
  )
}

export default AgentHubAddressInputFields
